import React, { useState, useEffect, useCallback, Dispatch } from 'react';

import block from 'propmods';

import './Select.scss';

const b = block('Select');

interface Option {
    value: string;
    text: string;
    namespace?: string;
}

interface SelectProps extends Omit<React.HTMLProps<HTMLSelectElement>, 'onChange'> {
    type: 'radio' | 'radiocheck';
    label?: string;
    className?: string;
    placeholder?: string;
    value?: string;
    disabled?: boolean;
    onChange: (value: string) => void;
    options: Option[];
    namespace?: string;
}

const registerOpenDropdownHandlers = ({
    activeIndex,
    setActiveIndex,
    setIsOpen,
    onOptionClick,
    options,
    namespace,
}: {
    activeIndex: number;
    setActiveIndex: Dispatch<number>;
    setIsOpen: Dispatch<boolean>;
    onOptionClick: (value: string) => void;
    options: Option[];
    namespace?: string;
}) => {
    const optionsLength = options.length;

    const keyDownCallback = (e: KeyboardEvent) => {
        switch (e.key) {
            case 'Up':
            case 'ArrowUp':
                e.preventDefault();
                setActiveIndex(activeIndex <= 0 ? optionsLength - 1 : activeIndex - 1);
                return;
            case 'Down':
            case 'ArrowDown':
                e.preventDefault();
                setActiveIndex(activeIndex + 1 === optionsLength ? 0 : activeIndex + 1);
                return;
            case 'Enter':
            case ' ': // Space
                e.preventDefault();
                onOptionClick(options[activeIndex].value);
                return;
            case 'Esc':
            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                return;
            case 'PageUp':
            case 'Home':
                e.preventDefault();
                setActiveIndex(0);
                return;
            case 'PageDown':
            case 'End':
                e.preventDefault();
                setActiveIndex(options.length - 1);
                return;
        }
    };

    const onClick = (e: Event) => {
        const target = e.composedPath().find((et) => {
            return et instanceof HTMLDivElement && et.dataset && et.dataset.namespace === namespace + '-dropdown-root';
        });

        if (!target) {
            // Click on outdoor place
            e.preventDefault();
            setIsOpen(false);
        }
    };

    document.addEventListener('keydown', keyDownCallback);
    document.addEventListener('click', onClick);
    return () => {
        document.removeEventListener('keydown', keyDownCallback);
        document.removeEventListener('click', onClick);
    };
};

const registerClosedDropdownHandlers = (toggleDropdownView: () => void) => {
    const keyDownCallback = (e: KeyboardEvent) => {
        switch (e.key) {
            case 'Up':
            case 'ArrowUp':
            case 'Down':
            case 'ArrowDown':
            case ' ': // Space
            case 'Enter':
                e.preventDefault();
                toggleDropdownView();
        }
    };
    document.addEventListener('keydown', keyDownCallback);
    return () => {
        document.removeEventListener('keydown', keyDownCallback);
    };
};

export const Select = ({
    options,
    label,
    value,
    onChange,
    type,
    placeholder,
    className,
    namespace,
    disabled,
}: SelectProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [isButtonFocused, setIsButtonFocused] = useState(false);

    const onOptionClick = useCallback(
        (value: string) => {
            if (value) {
                onChange && onChange(value);
            }
            setIsOpen(false);
        },
        [onChange, setIsOpen],
    );

    const toggleDropdownView = useCallback(
        (e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            e?.preventDefault();

            if (!isOpen) {
                const selectedIndex = options.findIndex((o) => o.value === value);
                setActiveIndex(selectedIndex < 0 ? 0 : selectedIndex);
            }
            setIsOpen((prev) => !prev);
        },
        [isOpen, options, value],
    );

    useEffect(() => {
        if (isOpen) {
            return registerOpenDropdownHandlers({
                activeIndex,
                setActiveIndex,
                setIsOpen,
                onOptionClick,
                options,
                namespace,
            });
        } else if (isButtonFocused) {
            return registerClosedDropdownHandlers(toggleDropdownView);
        }
    }, [isOpen, activeIndex, isButtonFocused, namespace, options, onOptionClick, toggleDropdownView]);

    const chosenOption = options.find((o) => o.value === value);

    return (
        <>
            <div {...b()} data-namespace={`${namespace}-dropdown-root`}>
                <button
                    disabled={disabled}
                    {...b('Button', [className], { type: `${type}_checked_${value ? 'yes' : 'no'}`, disabled })}
                    onClick={toggleDropdownView}
                    onFocus={() => setIsButtonFocused(true)}
                    onBlur={() => setIsButtonFocused(false)}
                    role="combobox"
                    aria-autocomplete="none"
                    aria-label={label}
                    aria-haspopup="listbox"
                    aria-controls={`${namespace}_dropdown`}
                    aria-expanded={isOpen}
                    aria-activedescendant={`${namespace}_element_${options[activeIndex]?.value}`}
                >
                    {!disabled && <span {...b('Button__Chevron', { direction: isOpen ? 'top' : 'bottom' })} />}
                    <span {...b('Button__Text', { disabled })}>
                        {chosenOption?.text || placeholder || options[0].text}
                    </span>
                </button>
                {isOpen && (
                    <ul {...b('List')} role="listbox" id={`${namespace}_dropdown`} tabIndex={-1}>
                        {options.map(({ text, value: optionValue }, index) => (
                            <li
                                key={optionValue}
                                id={`${namespace}_element_${optionValue}`}
                                aria-selected={chosenOption?.value === optionValue}
                                aria-checked={chosenOption?.value === optionValue}
                                aria-label={text}
                                role="checkbox"
                                onMouseOver={() => setActiveIndex(index)}
                                onClick={() => onOptionClick(optionValue)}
                                {...b('ListItem', {
                                    type: chosenOption?.value === optionValue ? 'checked' : '',
                                    active: index === activeIndex ? 'yes' : 'no',
                                })}
                            >
                                <span {...b('ListItem__Text')}>{text}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </>
    );
};
